/**
 * Solution Visual Studio client router.
 *
 * Provides two independent RouterProvider components — one for the editor
 * surface and one for the share surface. Each provider is wired to its own
 * route tree (EDITOR_ROUTES / SHARE_ROUTES) defined in routes.ts.
 *
 * Security invariants enforced here:
 *   - EditorRouterProvider only resolves routes from EDITOR_ROUTES.
 *     No share/viewer component is ever loaded through the editor router.
 *   - ShareRouterProvider only resolves routes from SHARE_ROUTES.
 *     No editor component is ever loaded through the share router.
 *   - Route matching is performed against the current pathname; the router
 *     does NOT perform Access or PIN validation — those are server-side
 *     responsibilities enforced by the Worker before the SPA is served.
 *
 * Requirements: SEC-01, SEC-04, VIEW-07, GOV-03
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  Suspense,
} from "react";

import {
  EDITOR_ROUTES,
  SHARE_ROUTES,
  type EditorRouteId,
  type RouteDescriptor,
  type ShareRouteId,
} from "@/client/routes";

// ---------------------------------------------------------------------------
// Route match result
// ---------------------------------------------------------------------------

interface RouteMatch<TId extends string> {
  route: RouteDescriptor<TId>;
  /** Extracted path params, e.g. { diagramId: "abc123" } */
  params: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Router context
// ---------------------------------------------------------------------------

interface RouterContextValue<TId extends string> {
  /** Current pathname (without search/hash). */
  pathname: string;
  /** Current matched route, or null if no match (should not happen with catch-all). */
  match: RouteMatch<TId> | null;
  /** Navigate to a new path using the History API. */
  navigate: (path: string) => void;
  /** Register an editor autosave guard before client-side navigation. */
  registerNavigationGuard?: (guard: NavigationGuard) => () => void;
}

/**
 * A route-level persistence guard. `flush` returns false when the latest local
 * change could not be persisted; the router then asks before discarding it.
 */
export interface NavigationGuard {
  /** `retryFailed: false` keeps navigation from repeatedly draining a failed draft. */
  flush: (options?: { retryFailed?: boolean }) => Promise<boolean>;
  hasUnsavedChanges: () => boolean;
}

// Two separate contexts — editor and share — so they cannot be confused.
const EditorRouterContext = createContext<RouterContextValue<EditorRouteId> | null>(null);
const ShareRouterContext = createContext<RouterContextValue<ShareRouteId> | null>(null);

// ---------------------------------------------------------------------------
// Path matching
// ---------------------------------------------------------------------------

/**
 * Match a pathname against a route pattern.
 * Supports a single `:param` segment per path segment.
 * Returns extracted params on match, or null on no match.
 * Patterns are matched in declaration order; the first match wins.
 */
function matchPattern(
  pattern: string,
  pathname: string
): Record<string, string> | null {
  // Wildcard catch-all matches everything.
  if (pattern === "*") return {};

  const patternSegments = pattern.split("/").filter(Boolean);
  const pathSegments = pathname.split("/").filter(Boolean);

  if (patternSegments.length !== pathSegments.length) return null;

  const params: Record<string, string> = {};

  for (let i = 0; i < patternSegments.length; i++) {
    const ps = patternSegments[i];
    const seg = pathSegments[i];

    if (ps.startsWith(":")) {
      params[ps.slice(1)] = decodeURIComponent(seg);
    } else if (ps !== seg) {
      return null;
    }
  }

  return params;
}

/**
 * Find the first matching route for the given pathname.
 * Routes are evaluated in declaration order; the catch-all ("*") must be last.
 */
function resolveMatch<TId extends string>(
  routes: ReadonlyArray<RouteDescriptor<TId>>,
  pathname: string
): RouteMatch<TId> | null {
  for (const route of routes) {
    const params = matchPattern(route.path, pathname);
    if (params !== null) {
      return { route, params };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Lazy component cache
// ---------------------------------------------------------------------------

/**
 * Cache of lazily-created React.lazy() wrappers keyed by route id.
 * Prevents re-creating the lazy wrapper on every render, which would cause
 * Suspense to re-suspend unnecessarily.
 */
const lazyComponentCache = new Map<string, React.LazyExoticComponent<React.ComponentType<object>>>();

function getLazyComponent<TId extends string>(
  route: RouteDescriptor<TId>
): React.LazyExoticComponent<React.ComponentType<object>> {
  const cached = lazyComponentCache.get(route.id);
  if (cached) return cached;
  const lazy = React.lazy(route.component);
  lazyComponentCache.set(route.id, lazy);
  return lazy;
}

// ---------------------------------------------------------------------------
// Generic router provider (internal)
// ---------------------------------------------------------------------------

interface RouterProviderProps<TId extends string> {
  routes: ReadonlyArray<RouteDescriptor<TId>>;
  Context: React.Context<RouterContextValue<TId> | null>;
  children?: React.ReactNode;
  /** Optional authenticated-surface wrapper for the matched route outlet. */
  RouteShell?: React.ComponentType<{ children: React.ReactNode }>;
  loadingFallback?: React.ReactNode;
  errorFallback?: React.ReactNode;
}

function RouterProviderInner<TId extends string>({
  routes,
  Context,
  children,
  RouteShell,
  loadingFallback,
}: RouterProviderProps<TId>) {
  const [pathname, setPathname] = useState(() =>
    window.location.pathname
  );

  const navigationGuardsRef = React.useRef(new Set<NavigationGuard>());
  const leaveCurrentRouteRef = React.useRef<Promise<boolean> | null>(null);
  const registerNavigationGuard = useCallback((guard: NavigationGuard) => {
    navigationGuardsRef.current.add(guard);
    return () => navigationGuardsRef.current.delete(guard);
  }, []);

  const canLeaveCurrentRoute = useCallback(async () => {
    if (leaveCurrentRouteRef.current) return leaveCurrentRouteRef.current;

    const leave = (async () => {
      for (const guard of navigationGuardsRef.current) {
        let flushed = false;
        try {
          flushed = await guard.flush({ retryFailed: false });
          if (!flushed && guard.hasUnsavedChanges()) {
            return window.confirm(
              "Your latest changes could not be saved. Leave this page and discard them?",
            );
          }
        } catch {
          // A guard failure must not create an unhandled navigation rejection.
          // Keep the editor mounted rather than assuming local work is safe.
          return false;
        }
      }
      return true;
    })();
    leaveCurrentRouteRef.current = leave;
    const clearLeave = () => {
      if (leaveCurrentRouteRef.current === leave) leaveCurrentRouteRef.current = null;
    };
    void leave.then(clearLeave, clearLeave);
    return leave;
  }, []);

  // Back/forward has already changed the address bar when popstate fires, so
  // defer rendering the new route until queued persistence has flushed. If the
  // user keeps editing, restore the current URL instead of unmounting the page.
  useEffect(() => {
    function onPopState() {
      const nextPath = window.location.pathname;
      void canLeaveCurrentRoute().then(
        (canLeave) => {
          if (canLeave) setPathname(nextPath);
          else window.history.pushState(null, "", pathname);
        },
        () => window.history.pushState(null, "", pathname),
      );
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [canLeaveCurrentRoute, pathname]);

  const navigate = useCallback((path: string) => {
    void canLeaveCurrentRoute().then(
      (canLeave) => {
        if (!canLeave) return;
        window.history.pushState(null, "", path);
        setPathname(new URL(path, window.location.origin).pathname);
      },
      () => undefined,
    );
  }, [canLeaveCurrentRoute]);

  const match = useMemo(
    () => resolveMatch(routes, pathname),
    [routes, pathname]
  );

  const contextValue = useMemo<RouterContextValue<TId>>(
    () => ({ pathname, match, navigate, registerNavigationGuard }),
    [pathname, match, navigate, registerNavigationGuard]
  );

  // Resolve the component to render for the current match.
  const RouteComponent = match ? getLazyComponent(match.route) : null;

  return (
    <Context.Provider value={contextValue}>
      {children}
      {RouteShell ? (
        <RouteShell>
          <Suspense fallback={loadingFallback ?? <SolutionVisualStudioRouterLoading />}>
            {match?.route.redirectTo ? (
              <SolutionVisualStudioRouterRedirect to={match.route.redirectTo} navigate={navigate} />
            ) : RouteComponent ? <RouteComponent /> : <SolutionVisualStudioRouterNoMatch />}
          </Suspense>
        </RouteShell>
      ) : (
        <Suspense fallback={loadingFallback ?? <SolutionVisualStudioRouterLoading />}>
          {match?.route.redirectTo ? (
            <SolutionVisualStudioRouterRedirect to={match.route.redirectTo} navigate={navigate} />
          ) : RouteComponent ? <RouteComponent /> : <SolutionVisualStudioRouterNoMatch />}
        </Suspense>
      )}
    </Context.Provider>
  );
}

// ---------------------------------------------------------------------------
// Default loading / no-match fallbacks
// ---------------------------------------------------------------------------

function SolutionVisualStudioRouterLoading() {
  return (
    <div
      role="status"
      aria-label="Loading"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
      }}
    >
      <span className="sr-only">Loading…</span>
    </div>
  );
}

function SolutionVisualStudioRouterNoMatch() {
  return (
    <div role="alert" style={{ padding: "2rem" }}>
      <p>Page not found.</p>
    </div>
  );
}

function SolutionVisualStudioRouterRedirect({ to, navigate }: { to: string; navigate: (path: string) => void }) {
  useEffect(() => {
    navigate(to);
  }, [navigate, to]);

  return <SolutionVisualStudioRouterLoading />;
}

// ---------------------------------------------------------------------------
// Public: EditorRouterProvider
//
// Mounts the editor route tree. Only EDITOR_ROUTES are resolved here.
// Must be used exclusively in the editor entrypoint (src/entrypoints/editor.tsx).
// Never mount this provider in the share entrypoint.
// ---------------------------------------------------------------------------

export interface EditorRouterProviderProps {
  /** Optional children rendered outside the route outlet (e.g., global overlays). */
  children?: React.ReactNode;
  /** Shared chrome applied to every authenticated editor route. */
  RouteShell?: React.ComponentType<{ children: React.ReactNode }>;
  /** Custom Suspense fallback shown while a route component is loading. */
  loadingFallback?: React.ReactNode;
}

/**
 * Router provider for the Access-protected editor surface.
 *
 * Resolves routes exclusively from EDITOR_ROUTES. No share/viewer component
 * is reachable through this provider.
 */
export function EditorRouterProvider({
  children,
  RouteShell,
  loadingFallback,
}: EditorRouterProviderProps) {
  return (
    <RouterProviderInner
      routes={EDITOR_ROUTES}
      Context={EditorRouterContext}
      RouteShell={RouteShell}
      loadingFallback={loadingFallback}
    >
      {children}
    </RouterProviderInner>
  );
}

// ---------------------------------------------------------------------------
// Public: ShareRouterProvider
//
// Mounts the share route tree. Only SHARE_ROUTES are resolved here.
// Must be used exclusively in the share entrypoint (src/entrypoints/share.tsx).
// Never mount this provider in the editor entrypoint.
// ---------------------------------------------------------------------------

export interface ShareRouterProviderProps {
  /** Optional children rendered outside the route outlet (e.g., noindex shell). */
  children?: React.ReactNode;
  /** Custom Suspense fallback shown while a route component is loading. */
  loadingFallback?: React.ReactNode;
}

/**
 * Router provider for the PIN-gated share viewer surface.
 *
 * Resolves routes exclusively from SHARE_ROUTES. No editor component is
 * reachable through this provider. The PIN gate route is the entry point;
 * the viewer route is only navigated to after the share API sets a valid
 * session cookie.
 */
export function ShareRouterProvider({
  children,
  loadingFallback,
}: ShareRouterProviderProps) {
  return (
    <RouterProviderInner
      routes={SHARE_ROUTES}
      Context={ShareRouterContext}
      loadingFallback={loadingFallback}
    >
      {children}
    </RouterProviderInner>
  );
}

// ---------------------------------------------------------------------------
// Public hooks
// ---------------------------------------------------------------------------

/**
 * Access the editor router context. Must be called within EditorRouterProvider.
 * Throws if called outside the editor surface — prevents accidental cross-surface use.
 */
export function useEditorRouter(): RouterContextValue<EditorRouteId> {
  const ctx = useContext(EditorRouterContext);
  if (!ctx) {
    throw new Error(
      "useEditorRouter must be used within EditorRouterProvider. " +
        "Do not call this hook from share/viewer components."
    );
  }
  return ctx;
}

/**
 * Access the share router context. Must be called within ShareRouterProvider.
 * Throws if called outside the share surface — prevents accidental cross-surface use.
 */
export function useShareRouter(): RouterContextValue<ShareRouteId> {
  const ctx = useContext(ShareRouterContext);
  if (!ctx) {
    throw new Error(
      "useShareRouter must be used within ShareRouterProvider. " +
        "Do not call this hook from editor components."
    );
  }
  return ctx;
}

/**
 * Convenience hook: returns the current route params from the editor router.
 */
export function useEditorParams(): Record<string, string> {
  return useEditorRouter().match?.params ?? {};
}

/**
 * Register a persistence guard for editor-only client-side navigation.
 * Share routes intentionally do not expose this API because they never own
 * mutable drafts.
 */
export function useEditorNavigationGuard(guard: NavigationGuard): void {
  const { registerNavigationGuard } = useEditorRouter();
  useEffect(() => {
    if (!registerNavigationGuard) return;
    return registerNavigationGuard(guard);
  }, [guard, registerNavigationGuard]);
}

/**
 * Convenience hook: returns the current route params from the share router.
 */
export function useShareParams(): Record<string, string> {
  return useShareRouter().match?.params ?? {};
}

// ---------------------------------------------------------------------------
// Public: RouterLink
//
// A lightweight anchor component that uses the History API instead of full
// page navigation. Surface-agnostic — pass the appropriate navigate function.
// ---------------------------------------------------------------------------

export interface RouterLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  navigate: (path: string) => void;
}

/**
 * Anchor element that navigates via the History API.
 * Use useEditorRouter().navigate or useShareRouter().navigate as the navigate prop.
 */
export function RouterLink({
  to,
  navigate,
  onClick,
  children,
  ...rest
}: RouterLinkProps) {
  function handleClick(e: React.MouseEvent<HTMLAnchorElement>) {
    // Allow modifier keys to open in new tab / trigger default browser behavior.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(to);
    onClick?.(e);
  }

  return (
    <a href={to} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}
